import { getRouteApi } from '@tanstack/react-router';
import { RefreshCcw } from 'lucide-react';
import { useState } from 'react';
import { BadgeIcon } from '~/components/BadgeIcon';
import { BadgeInfo } from '~/components/BadgeInfo';
import { useAuthData, useScopes } from '~/components/Providers/AuthProvider';
import { UserProfilePicture } from '~/components/UserProfilePicture';
import { Button } from '~/primitives/button';
import { Dialog, DialogContent, DialogTitle } from '~/primitives/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '~/primitives/popover';
import { ScrollArea } from '~/primitives/scroll-area';
import { cn, ModalProps } from '~/util';
import { useApiMutation } from '~/util/hooks/useApiMutation';
import { useLocaleDateString } from '~/util/hooks/useLocale';
import {
    editFeaturedBadge,
    syncBadgesMutation,
} from '~/util/mutations/public/users';
import {
    grantBadgeMutation,
    revokeBadgeMutation,
} from '~/util/mutations/staff/users';
import {
    allBadgesMap,
    FullBadgeData,
    FullBadgeDescription,
    singleBadges,
    tieredBadges,
} from '~/util/types/badges';

const Route = getRouteApi('/profile/user/$userid');

export function BadgesDialog(modalProps: ModalProps) {
    const profile = Route.useLoaderData({
        select: ({ profile }) => profile,
    });

    const { userid } = Route.useParams();

    const isOwnProfile = (useAuthData() ?? {})?.id === profile.id;

    const [selectedBadgeCode, setSelectedBadgeCode] = useState<string | null>(
        profile.featured_badge_code || null,
    );

    const allBadgesFull: Map<string, FullBadgeDescription> = new Map(
        allBadgesMap,
    );
    profile.badges?.forEach(({ badge_code, ...badge }) => {
        allBadgesFull.set(badge_code, {
            ...allBadgesFull.get(badge_code),
            ...badge,
        } as FullBadgeDescription);
    });

    const localeDate = useLocaleDateString();

    const tieredBadgesFull: FullBadgeData[][] = tieredBadges.map(
        ({ code, tiers }) =>
            tiers.map((tier) => {
                const badge_code = `${code}.${tier[0]}`;
                return {
                    badge_code,
                    ...(allBadgesFull.get(badge_code) as FullBadgeDescription),
                };
            }),
    );

    const tagsTieredBadges = tieredBadgesFull.filter((badges) =>
        badges[0]?.badge_code?.startsWith('global.level_tags'),
    );
    const globalTieredBadges = tieredBadgesFull.filter(
        (badges) => !badges[0]?.badge_code?.startsWith('global.level_tags'),
    );

    const singleBadgesFull = singleBadges.map(({ code }) => ({
        badge_code: code,
        ...(allBadgesFull.get(code) as FullBadgeDescription),
    }));

    const selectedBadge = allBadgesFull.get(selectedBadgeCode || '') || null;

    const syncMutation = useApiMutation(syncBadgesMutation());
    const editFeaturedBadgeMutation = useApiMutation(editFeaturedBadge());

    const grantMutation = useApiMutation(grantBadgeMutation());
    const revokeMutation = useApiMutation(revokeBadgeMutation());

    const { hasScope } = useScopes();

    return (
        <Dialog {...modalProps}>
            <DialogContent className="" aria-describedby={undefined}>
                <DialogTitle>Badges</DialogTitle>
                <div className="flex flex-col lg:flex-row w-full gap-2 mt-2">
                    <div className="lg:w-2/3 bg-background-darker rounded-2xl p-4">
                        <ScrollArea>
                            <div className="lg:max-h-[70vh]">
                                <h3 className="text-2xl font-semibold mb-2">
                                    Single Badges
                                </h3>
                                <div className="flex gap-4 flex-wrap mb-8">
                                    {singleBadgesFull.map((badge) => (
                                        <BadgeDisplay
                                            key={badge.badge_code}
                                            badges={[badge]}
                                            selectedBadgeCode={
                                                selectedBadgeCode
                                            }
                                            onSelectBadge={(badgeCode) =>
                                                setSelectedBadgeCode(badgeCode)
                                            }
                                        />
                                    ))}
                                </div>
                                <h3 className="text-2xl font-semibold mb-2">
                                    Global Badges
                                </h3>
                                <div className="flex gap-4 flex-wrap mb-8">
                                    {globalTieredBadges.map((tieredBadges) => (
                                        <BadgeDisplay
                                            key={tieredBadges[0].badge_code}
                                            badges={tieredBadges}
                                            selectedBadgeCode={
                                                selectedBadgeCode
                                            }
                                            onSelectBadge={(badgeCode) =>
                                                setSelectedBadgeCode(badgeCode)
                                            }
                                        />
                                    ))}
                                </div>

                                <h3 className="text-2xl font-semibold mb-2">
                                    Tags Badges
                                </h3>

                                <div className="flex gap-4 flex-wrap mb-8">
                                    {tagsTieredBadges.map((tieredBadges) => (
                                        <BadgeDisplay
                                            key={tieredBadges[0].badge_code}
                                            badges={tieredBadges}
                                            selectedBadgeCode={
                                                selectedBadgeCode
                                            }
                                            onSelectBadge={(badgeCode) =>
                                                setSelectedBadgeCode(badgeCode)
                                            }
                                        />
                                    ))}
                                </div>
                            </div>
                        </ScrollArea>
                    </div>
                    <div className="lg:w-1/3 bg-background-darker rounded-2xl p-4 flex items-center flex-col gap-2">
                        <div className="h-[30vh] w-full">
                            <div className="flex gap-2">
                                <div className="flex flex-col flex-1">
                                    <h2 className="w-fit border-foreground border-b-3 pb-2 mb-2">
                                        {selectedBadge?.title ??
                                            'No Badge Selected'}
                                    </h2>
                                    <p>{selectedBadge?.standard_description}</p>
                                    {selectedBadge?.description && (
                                        <p className="text-secondary-light">
                                            {selectedBadge?.additional_description?.(
                                                selectedBadge?.description,
                                            )}
                                        </p>
                                    )}
                                </div>
                                {selectedBadge && (
                                    <BadgeIcon
                                        badge={selectedBadge}
                                        className={cn(
                                            'size-25 overflow-hidden',
                                        )}
                                    />
                                )}
                            </div>

                            <p className="text-sm  text-secondary-light mt-2">
                                {selectedBadge?.unlocked_at
                                    ? `Unlocked on ${localeDate(selectedBadge?.unlocked_at)}`
                                    : 'Locked'}
                            </p>
                        </div>
                        {isOwnProfile && selectedBadgeCode != null && (
                            <div className="flex flex-col items-center gap-2">
                                <h2 className="">Preview</h2>
                                <UserProfilePicture
                                    className=""
                                    user={{
                                        ...profile,
                                        featured_badge_code: selectedBadgeCode,
                                    }}
                                    smallBadge
                                />
                                <div className="flex gap-2 justify-around mt-8">
                                    <Button
                                        onClick={() =>
                                            editFeaturedBadgeMutation.mutate(
                                                selectedBadgeCode,
                                            )
                                        }
                                        disabled={
                                            selectedBadge?.unlocked_at ==
                                            undefined
                                        }
                                    >
                                        Select Badge
                                    </Button>
                                    <Button variant="secondary">Cancel</Button>
                                </div>
                            </div>
                        )}
                        <div className="mt-auto flex flex-col gap-2">
                            {hasScope('user_modify') &&
                                selectedBadgeCode != null && (
                                    <div className="flex gap-2 ">
                                        <Button
                                            variant="confirm"
                                            onClick={() =>
                                                grantMutation.mutate({
                                                    userid,
                                                    badgeCode:
                                                        selectedBadgeCode,
                                                })
                                            }
                                        >
                                            Grant badge
                                        </Button>
                                        <Button
                                            variant="destructive"
                                            onClick={() =>
                                                revokeMutation.mutate({
                                                    userid,
                                                    badgeCode:
                                                        selectedBadgeCode,
                                                })
                                            }
                                        >
                                            Revoke badge
                                        </Button>
                                    </div>
                                )}
                            {(isOwnProfile || hasScope('user_modify')) && (
                                <Button
                                    variant="outline"
                                    onClick={() =>
                                        syncMutation.mutate({
                                            ownProfile: isOwnProfile,
                                            userid,
                                        })
                                    }
                                    className="gap-2"
                                >
                                    <RefreshCcw /> Sync Badges
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export function BadgeDisplay({
    badges,
    selectedBadgeCode,
    onSelectBadge,
}: {
    badges: FullBadgeData[];
    selectedBadgeCode: string | null;
    onSelectBadge: (badgeCode: string) => void;
}) {
    const [selectedTierCode, setSelectedTierCode] = useState<string | null>(
        () =>
            [...badges]
                .reverse()
                .find((badge) => badge.unlocked_at != undefined)?.badge_code ||
            badges[0]?.badge_code ||
            null,
    );

    const isGlobalSelected = selectedBadgeCode == selectedTierCode;

    const isSingleBadge = badges.length === 1;
    return (
        <Popover modal>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="relative h-20 hover:scale-110 pb-2 transition-transform duration-100"
                    onClick={() => {
                        if (isSingleBadge) {
                            onSelectBadge(badges[0].badge_code);
                            setSelectedTierCode(badges[0].badge_code);
                        }
                    }}
                    style={{
                        width: `${80 + (badges.length - 1) * 3}px`,
                    }}
                >
                    {badges.map((badge, index) => {
                        const isSelected =
                            badge.badge_code === selectedTierCode;

                        return (
                            <div
                                key={`${badge.badge_code}`}
                                className={cn(
                                    'absolute size-18 top-1/2 -translate-y-1/2 overflow-visible border-0 bg-transparent p-0 cursor-pointer',
                                )}
                                style={{
                                    left: index * 3,
                                    zIndex: isSelected
                                        ? badges.length + 1
                                        : index + 1,
                                }}
                                aria-label={badge.title}
                            >
                                <BadgeIcon
                                    badge={badge}
                                    title={badge.title}
                                    className={cn(
                                        'size-full overflow-hidden',
                                        badge?.unlocked_at == undefined &&
                                            'grayscale brightness-50',
                                        isSelected &&
                                            isGlobalSelected &&
                                            'scale-120',
                                    )}
                                />
                            </div>
                        );
                    })}
                </button>
            </PopoverTrigger>

            <PopoverContent
                side={isSingleBadge ? 'right' : 'bottom'}
                align={isSingleBadge ? 'center' : 'start'}
                className="flex flex-col gap-2 px-0 py-2 bg-background-semilight rounded-xl z-60 border border-secondary-light"
            >
                <ScrollArea>
                    <div className="max-h-[40vh]">
                        {badges.map((badge) => (
                            <BadgeInfo
                                key={badge.badge_code}
                                badge={badge}
                                selected={
                                    badge.badge_code === selectedTierCode &&
                                    isGlobalSelected
                                }
                                onClick={
                                    !isSingleBadge
                                        ? () => {
                                              onSelectBadge(badge.badge_code);
                                              setSelectedTierCode(
                                                  badge.badge_code,
                                              );
                                          }
                                        : undefined
                                }
                            />
                        ))}
                    </div>
                </ScrollArea>
            </PopoverContent>
        </Popover>
    );
}
